import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Plus, Trash2, Save, RefreshCw, Loader2, Edit2, X, Check,
  Truck, Euro, Globe
} from 'lucide-react'
import { shipping, ShippingCostData, ShippingCostRequest } from '@/lib/api'
import { useToast } from '@/components/ui/Toast'
import { CarrierLogo, CountryFlag } from '@/components/CarrierLogos'

// Transportistas disponibles
const CARRIERS = [
  { code: 'mrw', name: 'MRW' },
  { code: 'gls', name: 'GLS' },
  { code: 'ctt', name: 'CTT Express' },
  { code: 'cex', name: 'Correos Express' },
  { code: 'fedex', name: 'FedEx' },
]

// Países comunes
const COUNTRIES = [
  { code: '*', name: 'Todos los países' },
  { code: 'ES', name: 'España' },
  { code: 'PT', name: 'Portugal' },
  { code: 'FR', name: 'Francia' },
  { code: 'DE', name: 'Alemania' },
  { code: 'IT', name: 'Italia' },
  { code: 'GB', name: 'Reino Unido' },
  { code: 'BE', name: 'Bélgica' },
  { code: 'NL', name: 'Países Bajos' },
  { code: 'AT', name: 'Austria' },
  { code: 'PL', name: 'Polonia' },
  { code: 'IE', name: 'Irlanda' },
]

// Tramos de peso predefinidos
const WEIGHT_RANGES = [
  { from: 0, to: 1, label: '0-1 kg' },
  { from: 1, to: 2, label: '1-2 kg' },
  { from: 2, to: 5, label: '2-5 kg' },
  { from: 5, to: 10, label: '5-10 kg' },
  { from: 10, to: 20, label: '10-20 kg' },
  { from: 20, to: 30, label: '20-30 kg' },
  { from: 30, to: 50, label: '30-50 kg' },
]

interface EditingCost {
  id?: number
  carrier_code: string
  country_code: string
  weight_from: number
  weight_to: number
  cost: number
  is_active: boolean
}

const emptyForm: EditingCost = {
  carrier_code: 'mrw',
  country_code: 'ES',
  weight_from: 0,
  weight_to: 1,
  cost: 0,
  is_active: true,
}

export default function ShippingCostsPage() {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [filterCarrier, setFilterCarrier] = useState<string>('')
  const [filterCountry, setFilterCountry] = useState<string>('')
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editForm, setEditForm] = useState<EditingCost>(emptyForm)
  const [showAddForm, setShowAddForm] = useState(false)

  // Query para listar costes
  const costsQuery = useQuery({
    queryKey: ['shipping-costs', filterCarrier, filterCountry],
    queryFn: () => shipping.getShippingCosts({
      carrier_code: filterCarrier || undefined,
      country_code: filterCountry || undefined,
    }),
  })

  // Mutation para crear coste
  const createMutation = useMutation({
    mutationFn: (cost: ShippingCostRequest) => shipping.createShippingCost(cost),
    onSuccess: () => {
      toast.success('Coste creado correctamente')
      queryClient.invalidateQueries({ queryKey: ['shipping-costs'] })
      setShowAddForm(false)
      setEditForm(emptyForm)
    },
    onError: (error: any) => {
      toast.error('Error al crear coste', error.response?.data?.detail || error.message)
    },
  })

  // Mutation para actualizar coste
  const updateMutation = useMutation({
    mutationFn: ({ id, cost }: { id: number; cost: ShippingCostRequest }) =>
      shipping.updateShippingCost(id, cost),
    onSuccess: () => {
      toast.success('Coste actualizado correctamente')
      queryClient.invalidateQueries({ queryKey: ['shipping-costs'] })
      setEditingId(null)
    },
    onError: (error: any) => {
      toast.error('Error al actualizar coste', error.response?.data?.detail || error.message)
    },
  })

  // Mutation para eliminar coste
  const deleteMutation = useMutation({
    mutationFn: (id: number) => shipping.deleteShippingCost(id),
    onSuccess: () => {
      toast.success('Coste eliminado correctamente')
      queryClient.invalidateQueries({ queryKey: ['shipping-costs'] })
    },
    onError: (error: any) => {
      toast.error('Error al eliminar coste', error.response?.data?.detail || error.message)
    },
  })

  const handleSaveNew = () => {
    createMutation.mutate({
      carrier_code: editForm.carrier_code,
      country_code: editForm.country_code,
      weight_from: editForm.weight_from,
      weight_to: editForm.weight_to,
      cost: editForm.cost,
      is_active: editForm.is_active,
    })
  }

  const handleSaveEdit = () => {
    if (editingId === null) return
    updateMutation.mutate({
      id: editingId,
      cost: {
        carrier_code: editForm.carrier_code,
        country_code: editForm.country_code,
        weight_from: editForm.weight_from,
        weight_to: editForm.weight_to,
        cost: editForm.cost,
        is_active: editForm.is_active,
      },
    })
  }

  const startEdit = (cost: ShippingCostData) => {
    setEditingId(cost.id)
    setEditForm({
      id: cost.id,
      carrier_code: cost.carrier_code,
      country_code: cost.country_code,
      weight_from: cost.weight_from,
      weight_to: cost.weight_to,
      cost: cost.cost,
      is_active: cost.is_active,
    })
  }

  const cancelEdit = () => {
    setEditingId(null)
    setEditForm(emptyForm)
  }

  const costs = costsQuery.data?.costs || []

  // Agrupar costes por transportista para mejor visualización
  const costsByCarrier = CARRIERS.map(carrier => ({
    ...carrier,
    costs: costs.filter(c => c.carrier_code === carrier.code),
  }))

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
              Costes de Envío
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1">
              Configura los costes de envío por transportista, país y tramo de peso
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => costsQuery.refetch()}
              disabled={costsQuery.isFetching}
              className="p-2 text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 rounded-lg hover:bg-white dark:hover:bg-slate-800"
            >
              <RefreshCw className={`w-5 h-5 ${costsQuery.isFetching ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={() => {
                setShowAddForm(true)
                setEditForm(emptyForm)
              }}
              className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
            >
              <Plus className="w-4 h-4" />
              Añadir coste
            </button>
          </div>
        </div>

        {/* Filtros */}
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-4 mb-6">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Truck className="w-4 h-4 text-slate-400" />
              <select
                value={filterCarrier}
                onChange={(e) => setFilterCarrier(e.target.value)}
                className="px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm"
              >
                <option value="">Todos los transportistas</option>
                {CARRIERS.map(c => (
                  <option key={c.code} value={c.code}>{c.name}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <Globe className="w-4 h-4 text-slate-400" />
              <select
                value={filterCountry}
                onChange={(e) => setFilterCountry(e.target.value)}
                className="px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm"
              >
                <option value="">Todos los países</option>
                {COUNTRIES.slice(1).map(c => (
                  <option key={c.code} value={c.code}>{c.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Formulario de nuevo coste */}
        {showAddForm && (
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-teal-200 dark:border-teal-700 p-4 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                Nuevo coste de envío
              </h3>
              <button
                onClick={() => setShowAddForm(false)}
                className="p-1 text-slate-400 hover:text-slate-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="grid grid-cols-6 gap-4 items-end">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Transportista
                </label>
                <select
                  value={editForm.carrier_code}
                  onChange={(e) => setEditForm({ ...editForm, carrier_code: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700"
                >
                  {CARRIERS.map(c => (
                    <option key={c.code} value={c.code}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  País
                </label>
                <select
                  value={editForm.country_code}
                  onChange={(e) => setEditForm({ ...editForm, country_code: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700"
                >
                  {COUNTRIES.map(c => (
                    <option key={c.code} value={c.code}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Peso desde (kg)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.1"
                  value={editForm.weight_from}
                  onChange={(e) => setEditForm({ ...editForm, weight_from: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Peso hasta (kg)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.1"
                  value={editForm.weight_to}
                  onChange={(e) => setEditForm({ ...editForm, weight_to: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Coste (EUR)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={editForm.cost}
                  onChange={(e) => setEditForm({ ...editForm, cost: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700"
                />
              </div>
              <div>
                <button
                  onClick={handleSaveNew}
                  disabled={createMutation.isPending}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50"
                >
                  {createMutation.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  Guardar
                </button>
              </div>
            </div>
            {/* Selector rápido de tramos */}
            <div className="mt-4 flex items-center gap-2 flex-wrap">
              <span className="text-xs text-slate-500">Tramos rápidos:</span>
              {WEIGHT_RANGES.map(range => (
                <button
                  key={range.label}
                  onClick={() => setEditForm({ ...editForm, weight_from: range.from, weight_to: range.to })}
                  className={`px-2 py-1 text-xs rounded border ${
                    editForm.weight_from === range.from && editForm.weight_to === range.to
                      ? 'bg-teal-100 border-teal-300 text-teal-700'
                      : 'border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700'
                  }`}
                >
                  {range.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Lista de costes agrupada por transportista */}
        {costsQuery.isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 text-teal-600 animate-spin" />
          </div>
        ) : costs.length === 0 ? (
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-8 text-center">
            <Euro className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
              No hay costes configurados
            </h3>
            <p className="text-slate-500 dark:text-slate-400 mb-4">
              Añade costes de envío para poder calcular el precio de cada paquete.
            </p>
            <button
              onClick={() => setShowAddForm(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
            >
              <Plus className="w-4 h-4" />
              Añadir primer coste
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {costsByCarrier.filter(c => c.costs.length > 0 || !filterCarrier).map(carrier => (
              carrier.costs.length > 0 && (
                <div key={carrier.code} className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
                  {/* Header del transportista */}
                  <div className="flex items-center gap-3 px-4 py-3 bg-slate-50 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-700">
                    <CarrierLogo carrier={carrier.code} className="h-5 w-16" />
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                      {carrier.name}
                    </span>
                    <span className="text-xs text-slate-500">
                      ({carrier.costs.length} tarifas)
                    </span>
                  </div>

                  {/* Tabla de costes */}
                  <table className="w-full">
                    <thead>
                      <tr className="text-xs font-medium text-slate-500 uppercase border-b border-slate-100 dark:border-slate-700/50">
                        <th className="px-4 py-2 text-left">País</th>
                        <th className="px-4 py-2 text-left">Tramo de peso</th>
                        <th className="px-4 py-2 text-right">Coste</th>
                        <th className="px-4 py-2 text-center">Estado</th>
                        <th className="px-4 py-2 text-right">Acciones</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-700/50">
                      {carrier.costs.map(cost => (
                        <tr key={cost.id} className="hover:bg-slate-50 dark:hover:bg-slate-900/30">
                          {editingId === cost.id ? (
                            // Modo edición
                            <>
                              <td className="px-4 py-2">
                                <select
                                  value={editForm.country_code}
                                  onChange={(e) => setEditForm({ ...editForm, country_code: e.target.value })}
                                  className="w-full px-2 py-1 text-sm rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700"
                                >
                                  {COUNTRIES.map(c => (
                                    <option key={c.code} value={c.code}>{c.name}</option>
                                  ))}
                                </select>
                              </td>
                              <td className="px-4 py-2">
                                <div className="flex items-center gap-2">
                                  <input
                                    type="number"
                                    min="0"
                                    step="0.1"
                                    value={editForm.weight_from}
                                    onChange={(e) => setEditForm({ ...editForm, weight_from: parseFloat(e.target.value) || 0 })}
                                    className="w-20 px-2 py-1 text-sm rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700"
                                  />
                                  <span className="text-slate-400">-</span>
                                  <input
                                    type="number"
                                    min="0"
                                    step="0.1"
                                    value={editForm.weight_to}
                                    onChange={(e) => setEditForm({ ...editForm, weight_to: parseFloat(e.target.value) || 0 })}
                                    className="w-20 px-2 py-1 text-sm rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700"
                                  />
                                  <span className="text-xs text-slate-500">kg</span>
                                </div>
                              </td>
                              <td className="px-4 py-2">
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={editForm.cost}
                                  onChange={(e) => setEditForm({ ...editForm, cost: parseFloat(e.target.value) || 0 })}
                                  className="w-24 px-2 py-1 text-sm text-right rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700"
                                />
                              </td>
                              <td className="px-4 py-2 text-center">
                                <button
                                  onClick={() => setEditForm({ ...editForm, is_active: !editForm.is_active })}
                                  className={`px-2 py-1 text-xs rounded ${
                                    editForm.is_active
                                      ? 'bg-emerald-100 text-emerald-700'
                                      : 'bg-slate-100 text-slate-500'
                                  }`}
                                >
                                  {editForm.is_active ? 'Activo' : 'Inactivo'}
                                </button>
                              </td>
                              <td className="px-4 py-2">
                                <div className="flex items-center justify-end gap-1">
                                  <button
                                    onClick={handleSaveEdit}
                                    disabled={updateMutation.isPending}
                                    className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded"
                                  >
                                    {updateMutation.isPending ? (
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                      <Check className="w-4 h-4" />
                                    )}
                                  </button>
                                  <button
                                    onClick={cancelEdit}
                                    className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                </div>
                              </td>
                            </>
                          ) : (
                            // Modo visualización
                            <>
                              <td className="px-4 py-2">
                                <div className="flex items-center gap-2">
                                  {cost.country_code === '*' ? (
                                    <span className="text-sm text-slate-600 dark:text-slate-400">
                                      Todos
                                    </span>
                                  ) : (
                                    <>
                                      <CountryFlag countryCode={cost.country_code} />
                                      <span className="text-sm text-slate-600 dark:text-slate-400">
                                        {COUNTRIES.find(c => c.code === cost.country_code)?.name || cost.country_code}
                                      </span>
                                    </>
                                  )}
                                </div>
                              </td>
                              <td className="px-4 py-2">
                                <span className="text-sm text-slate-600 dark:text-slate-400">
                                  {cost.weight_from} - {cost.weight_to} kg
                                </span>
                              </td>
                              <td className="px-4 py-2 text-right">
                                <span className="text-sm font-medium text-slate-900 dark:text-white">
                                  {cost.cost.toFixed(2)} EUR
                                </span>
                              </td>
                              <td className="px-4 py-2 text-center">
                                <span className={`px-2 py-1 text-xs rounded ${
                                  cost.is_active
                                    ? 'bg-emerald-100 text-emerald-700'
                                    : 'bg-slate-100 text-slate-500'
                                }`}>
                                  {cost.is_active ? 'Activo' : 'Inactivo'}
                                </span>
                              </td>
                              <td className="px-4 py-2">
                                <div className="flex items-center justify-end gap-1">
                                  <button
                                    onClick={() => startEdit(cost)}
                                    className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded"
                                    title="Editar"
                                  >
                                    <Edit2 className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => {
                                      if (confirm('¿Eliminar este coste?')) {
                                        deleteMutation.mutate(cost.id)
                                      }
                                    }}
                                    className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded"
                                    title="Eliminar"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </td>
                            </>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
